import { Bot, Trash2, Loader2, AlertTriangle, RefreshCw, Square, X } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useMiranda } from '@/contexts/MirandaContext';
import { useMirandaChat } from '@/hooks/useMirandaChat';
import { ChatInput } from '@/components/miranda/ChatInput';
import { cn } from '@/lib/utils';
import { useRef, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { agentApi, api } from '@/services/api';
import { MarkdownMessage } from '@/components/miranda/MarkdownMessage';
import { ToolCallCard, type ToolCallRecord } from '@/components/ai-elements';
import { RichChat } from '@/components/miranda-chat';
import { RICH_CHAT_AGENT_ID, usesRichChat } from '@/services/miranda-rich-chat';
import { agentSelectorOptions } from '@/lib/miranda-agents';
import { scrollViewportToBottom } from './scroll-to-bottom';
import type { MirandaFocus } from '@/lib/miranda-focus';

/**
 * La sessió desada ja no existeix (esborrada des de `/chat`) o no és d'aquesta persona: el backend
 * respon 404. Qualsevol altre error (sense xarxa, un 500) no esborra res: la conversa segueix.
 */
export function isMissingSessionError(error: unknown): boolean {
  return (error as { response?: { status?: number } } | null)?.response?.status === 404;
}

/**
 * A3b — Text principal del xip de focus. Una conversa de WhatsApp no porta cap reserva a l'URL
 * (`beds24Ids` és buit): abans el xip deia «#» i prou. Sense id ni nom: el xip es veu a
 * pantalla i la conversa ja és a la columna del costat.
 */
export function focusChipLabel(focus: MirandaFocus): string {
  if (focus.type === 'whatsapp-conversation') return 'Conversa de WhatsApp';
  return `#${focus.beds24Ids[0]}`;
}

/**
 * A3b — El full va al costat (no modal, columna dreta) només quan hi ha una fitxa de reserva
 * oberta, que és el que el mode costat a costat reparteix. A `/whatsapp/<id>` no hi ha fitxa:
 * el full es comporta com a qualsevol altra pàgina.
 */
export function isSideBySideFocus(focus: MirandaFocus | null): boolean {
  return focus !== null && focus.type !== 'whatsapp-conversation';
}

/**
 * Miranda Sheet - Slide-over panel for AI chat
 * Opens from the right side of the screen
 */
export function MirandaSheet() {
  const {
    isOpen,
    close,
    agentId,
    setAgentId,
    tokensUsed,
    clearSession,
    currentPage,
    routeFocus,
    focus,
    focusDismissed,
    setFocusDismissed,
    sessionId: storedSessionId,
    setSessionId,
    setMessages,
    conversationKey,
    updateTokenUsage,
  } = useMiranda();

  // Amb la Miranda única triada, el cos del giny és el xat ric (targetes, «Atura» de debò); amb
  // qualsevol altre agent, el client de text de sempre. La capçalera és la mateixa per a tots dos.
  const richChat = usesRichChat(agentId);

  // 🆕 F2 — Amb una fitxa de reserva oberta, Miranda i la fitxa s'han de veure a la vegada.
  // El `Sheet` passa a `modal={false}` (Radix no pinta l'overlay en mode no modal), i a
  // `≥ lg` la fitxa es desplaça 380 px cap a l'esquerra: dues columnes, cap superposició.
  const sideBySide = isSideBySideFocus(routeFocus);
  const isConversationFocus = routeFocus?.type === 'whatsapp-conversation';
  const focusNoun = isConversationFocus ? 'aquesta conversa' : 'aquesta reserva';

  // El nom de l'hoste per al xip surt de la mateixa clau que ja ha omplert `CalendarV2Page` en obrir
  // la fitxa, i amb la mateixa crida: amb `staleTime: Infinity` i la dada a la cau, el panell no en fa
  // cap de pròpia. Abans era `queryFn: skipToken`: quan algú invalidava la clau (el xat després
  // d'escriure, una targeta, afegir una habitació al grup) i les opcions de la consulta eren les
  // d'aquest panell, la refrescada fallava («Missing queryFn») i el calendari ho llegia com a «No
  // s'ha trobat la reserva», tancava la fitxa i el giny (prova del 14-OCT-2026).
  const primaryBeds24Id = routeFocus?.beds24Ids[0] ?? null;
  const { data: focusReservation } = useQuery<{ guest_name?: string }>({
    queryKey: ['reservation-by-beds24', primaryBeds24Id],
    queryFn: async () => {
      const res = await api.get(`/reservations/by-beds24/${primaryBeds24Id}`);
      return res.data;
    },
    enabled: !!primaryBeds24Id && !isConversationFocus,
    staleTime: Infinity,
    retry: false,
  });
  const focusGuestName = focusReservation?.guest_name?.trim() || null;

  // Usar useMirandaChat per gestionar el chat
  const {
    messages,
    input,
    setInput,
    isLoading,
    error,
    pendingFiles,
    addFiles,
    removeFile,
    handleSubmit,
    reload,
    stop,
    sessionId,
  } = useMirandaChat();

  const { data: availableAgents } = useQuery({
    queryKey: ['agents'],
    queryFn: agentApi.getAgents,
  });
  // Amb la Miranda única a la llista, només ella (i l'agent vell d'una conversa desada, fins a
  // «Netejar»); amb el fre, tots. Amb una sola opció no hi ha res a triar i el selector no es pinta:
  // la capçalera ja diu «Miranda».
  const agentOptions = agentSelectorOptions(availableAgents, agentId);
  const showAgentSelect = agentOptions.length > 1;

  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll al final quan arriben missatges o text en streaming (`messages` canvia a cada
  // tros). C1 (17-SET): el `ref` de `ScrollArea` apunta a l'arrel de Radix, que és
  // `overflow-hidden` i no fa scroll; el que fa scroll és el Viewport de dins.
  useEffect(() => {
    scrollViewportToBottom(scrollRef.current);
  }, [messages, isLoading]);

  // 🆕 F1 — Recuperar la conversa després d'una càrrega completa (enllaç a /reserves/:ids o
  // recàrrega). El `sessionId` és a localStorage; els missatges els guarda el backend, i es
  // demanen només quan s'obre el panell, no a cada pàgina.
  // El xat ric es recupera sol (`GET /agent/sessions/:id/ui`, filtrat): aquí no s'hi demana res.
  const needsRestore = isOpen && !richChat && !!storedSessionId && messages.length === 0 && !isLoading;
  const { data: restoredSession, isError: restoreFailed } = useQuery({
    queryKey: ['agent-session', storedSessionId],
    queryFn: () => agentApi.getSession(storedSessionId!),
    enabled: needsRestore,
    retry: false,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!needsRestore || !restoredSession?.messages?.length) return;
    setMessages(
      restoredSession.messages
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .map((m) => ({
          id: m.id,
          role: m.role as 'user' | 'assistant',
          content: m.content,
          // S3 — sense això les targetes de tool call no tornen mai: el backend les desa a
          // `chat_messages.tool_calls` i aquí es llençaven en recuperar la conversa.
          toolCalls: Array.isArray(m.toolCalls) ? m.toolCalls : undefined,
          createdAt: new Date(m.createdAt),
        }))
    );
  }, [needsRestore, restoredSession, setMessages]);

  // La sessió desada ja no existeix (esborrada des de /chat): es comença de nou, en silenci.
  useEffect(() => {
    if (restoreFailed) setSessionId(null);
  }, [restoreFailed, setSessionId]);

  // El mateix per al xat ric, però només amb un 404: tornar-lo a muntar sense sessió (Netejar).
  const handleRichHistoryError = useCallback(
    (err: unknown) => {
      if (isMissingSessionError(err)) clearSession();
    },
    [clearSession]
  );

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && close()} modal={!sideBySide}>
      <SheetContent
        side="right"
        // La X del primitiu és `absolute right-4` i la capçalera `px-4`: quedava just damunt
        // de la fletxa del selector d'agent a totes les amplades (informe del 15-SET, §12.3).
        // Es pinta a la capçalera, com fa la fitxa de reserva.
        hideClose
        // `data-sheet-outside-safe`: la `ReservationSheet` és `modal={false}` i el seu
        // `onInteractOutside` la tancaria (i navegaria a `/calendar-v2`) en clicar aquí.
        // Amb aquest atribut, escriure a Miranda no fa perdre la fitxa ni el focus.
        data-sheet-outside-safe
        onInteractOutside={(e) => {
          // En mode costat a costat, clicar la fitxa o el calendari no tanca Miranda: es
          // tanca amb la seva X, amb el botó flotant o amb Esc.
          if (sideBySide) e.preventDefault();
        }}
        className={cn(
          'flex flex-col p-0',
          sideBySide
            ? // Columna dreta fixa. L'offset superior és el mateix que el de la fitxa,
              // perquè la capçalera del calendari (pestanyes i barra de grup) segueixi
              // clicable amb totes dues obertes.
              //
              // El repartiment arrenca a `md`, no a `lg`: fins al 15-SET la fitxa no
              // s'apartava per sota de 1024 px i tots dos panells quedaven ancorats a la
              // dreta, un damunt de l'altre (§11 de `plans/miranda-grups-i-torns.md`). A `md`
              // hi caben 340 + 384 = 724 px dins dels 768; a `lg` es tornen a eixamplar.
              'w-full sm:max-w-md md:max-w-[340px] md:mt-[120px] md:h-[calc(100%-120px)] lg:max-w-[380px]'
            : 'w-full sm:max-w-md md:max-w-lg'
        )}
      >
        {/* Header */}
        <SheetHeader className="px-4 py-3 border-b bg-muted/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-primary" />
              <SheetTitle className="text-base">Miranda</SheetTitle>
            </div>

            {/* Selector d'agent i tancar, en aquest ordre i sense solapar-se */}
            <div className="flex items-center gap-1">
              {showAgentSelect && (
                <Select value={agentId} onValueChange={setAgentId}>
                  <SelectTrigger className="w-[140px] h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {agentOptions.map((agent) => (
                      <SelectItem key={agent.id} value={agent.id}>
                        {agent.name.replace('Miranda ', '')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={close}
                aria-label="Tancar"
              >
                <X className="h-4 w-4 text-muted-foreground" />
              </Button>
            </div>
          </div>

          {/* Pàgina actual i accions */}
          {/*
            C1 (17-SET): el xip no es podia encongir (flex item sense `min-w-0`) i les accions
            tampoc tenien `shrink-0`: en aparèixer «Atura», el xip passava per sota del botó.
          */}
          <div className="flex items-center justify-between gap-2 mt-2">
            <div className="flex items-center gap-2 min-w-0">
              {/*
                🆕 F2 — Xip de context. Diu quina reserva viatja amb la pregunta, i clicar-lo
                la treu ("pregunta sense context"). Es recupera sol en canviar de reserva:
                descartar el context és per a una pregunta, no per a sempre.
              */}
              {routeFocus ? (
                <button
                  type="button"
                  onClick={() => setFocusDismissed(!focusDismissed)}
                  title={
                    focusDismissed
                      ? `Torna a preguntar sobre ${focusNoun}`
                      : `Pregunta sense el context d'${focusNoun}`
                  }
                  className={cn(
                    'flex min-w-0 items-center gap-1 max-w-[220px] rounded-full border px-2 py-0.5 text-xs transition-colors',
                    focusDismissed
                      ? 'border-dashed text-muted-foreground hover:bg-muted'
                      : 'border-primary/40 bg-primary/10 text-primary hover:bg-primary/20'
                  )}
                >
                  <span className={cn('shrink-0', !isConversationFocus && 'font-mono')}>
                    {focusChipLabel(routeFocus)}
                  </span>
                  {!isConversationFocus && routeFocus.beds24Ids.length > 1 && (
                    <span className="text-[10px] shrink-0">+{routeFocus.beds24Ids.length - 1}</span>
                  )}
                  {!isConversationFocus && focusGuestName && <span className="truncate">· {focusGuestName}</span>}
                  {focusDismissed ? (
                    <span className="text-[10px] whitespace-nowrap shrink-0">· sense context</span>
                  ) : (
                    <X className="h-3 w-3 shrink-0 opacity-60" />
                  )}
                </button>
              ) : (
                <Badge variant="outline" className="text-xs">
                  {currentPage}
                </Badge>
              )}
              {tokensUsed > 0 && (
                <span className="min-w-0 text-xs font-mono text-muted-foreground whitespace-nowrap truncate">
                  {tokensUsed.toLocaleString()} tokens
                </span>
              )}
            </div>

            <div className="flex items-center gap-1 shrink-0">
              {/* El xat ric porta el seu «Atura» damunt de l'entrada: aquí no se'n pinta un segon. */}
              {!richChat && isLoading && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={stop}
                  className="h-7 text-xs text-muted-foreground hover:text-destructive"
                >
                  <Square className="h-3 w-3 mr-1" />
                  Atura
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={clearSession}
                className="h-7 text-xs text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="h-3 w-3 mr-1" />
                Netejar
              </Button>
            </div>
          </div>
        </SheetHeader>

        {richChat ? (
          // La sessió viu a localStorage (context): en obrir el giny, `RichChat` la recupera; una
          // conversa nova (Netejar, un altre agent) en canvia la `key` i el torna a muntar buit.
          <RichChat
            key={`${RICH_CHAT_AGENT_ID}-${conversationKey}`}
            agentId={RICH_CHAT_AGENT_ID}
            agentDescription="Pregunta'm sobre reserves, viatgers o qualsevol cosa del sistema."
            sessionId={storedSessionId}
            onSessionId={setSessionId}
            onUsage={updateTokenUsage}
            onHistoryError={handleRichHistoryError}
            focus={focus}
            compact
          />
        ) : (
        <>
        {/* Messages Area */}
        <ScrollArea ref={scrollRef} className="flex-1 px-4 py-3">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
              <Bot className="h-12 w-12 mb-4 opacity-20" />
              <p className="text-sm">Hola! Sóc Miranda, l'assistent IA.</p>
              <p className="text-xs mt-1">
                Pregunta'm sobre reserves, viatgers o qualsevol cosa del sistema.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={cn(
                    'flex',
                    message.role === 'user' ? 'justify-end' : 'justify-start'
                  )}
                >
                  <div
                    className={cn(
                      'max-w-[85%] rounded-lg px-3 py-2 text-sm',
                      message.role === 'user'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted'
                    )}
                  >
                    {message.role === 'assistant' ? (
                      <MarkdownMessage content={message.content} />
                    ) : (
                      message.content
                    )}

                    {/*
                      🆕 S3 — Targetes de tool call sota el missatge de l'assistent. Abans hi
                      havia un bloc que llegia `toolInvocations` (nom de l'AI SDK v4): cap camí
                      del codi omplia mai aquest camp, i el panell no ensenyava ni una sola tool.
                      Ara es llegeix `toolCalls`, que és el que desa el backend i el que torna
                      `agentApi.getSession` en recuperar la conversa.

                      El que encara no es veu en viu: l'SSE d'ara no envia les tool calls mentre
                      corren (només text, amb les línies «🔧 Executant …»), i el missatge de
                      l'assistent es construeix al navegador. Les targetes apareixen quan la
                      conversa es recupera del backend. Canviar-ho vol tocar l'stream (S9).
                    */}
                    {(() => {
                      const calls = message.toolCalls as ToolCallRecord[] | undefined;
                      if (!calls?.length) return null;
                      return (
                        <div className="mt-2 space-y-1">
                          {calls.map((call, idx) => (
                            <ToolCallCard key={idx} call={call} />
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              ))}

              {/* Streaming indicator */}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-muted rounded-lg px-3 py-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950 rounded text-sm text-red-600 dark:text-red-400">
              <AlertTriangle className="h-4 w-4" />
              <span>{error.message}</span>
              <Button variant="ghost" size="sm" onClick={() => reload()}>
                <RefreshCw className="h-3 w-3 mr-1" />
                Reintentar
              </Button>
            </div>
          )}
        </ScrollArea>

        {/* Input Area */}
        <div className="border-t p-3">
          <ChatInput
            input={input}
            onInputChange={setInput}
            onSubmit={handleSubmit}
            isLoading={isLoading}
            placeholder="Escriu un missatge..."
            pendingFiles={pendingFiles}
            onAddFiles={addFiles}
            onRemoveFile={removeFile}
          />

          {/* Session ID indicator */}
          {sessionId && (
            <p className="text-[10px] text-muted-foreground mt-1 text-center font-mono">
              Session: {sessionId.slice(0, 8)}...
            </p>
          )}
        </div>
        </>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default MirandaSheet;
